/**
 * matte-edge.js — the two edge controls every keyer has and this tool did not:
 * FEATHER (soften the cutout edge) and CHOKE (pull it in / push it out).
 *
 * WHY THEY MATTER MORE THAN A BETTER MODEL. The complaint about an automatic
 * cutout is almost never "it found the wrong object" — it is a one-to-three
 * pixel rim of the old background still clinging to the subject, or a hard
 * aliased edge that reads as a sticker when laid over anything. Both are edge
 * problems, both are fixed by moving the alpha threshold a pixel and blurring
 * it slightly, and neither needs the network to run again.
 *
 * WHERE IT SITS. Between the matte and the compositor, on the ALPHA channel
 * only — so it applies identically to a neural matte and a colour key, it is
 * re-appliable at export time from the stored matte, and a slider drag shows
 * its result on the current frame instantly instead of costing another pass.
 *
 * HOW. One separable box blur of alpha, then a linear remap of the blurred
 * value — not a blur for feather plus a morphological erode for choke. The
 * remap gives both from the SAME blur:
 *
 *      a' = clamp( (B - centre) / width + 0.5 )
 *
 *   • `width`  wide  → the ramp from 0 to 1 spans more pixels  = feather
 *   • `centre` above 0.5 → the 50 % contour sits where alpha WAS higher, i.e.
 *     further inside the subject                                = choke in
 *   • `centre` below 0.5 → the contour moves outward            = choke out
 *
 * A true erode/dilate would need a second pass and a min/max window, and would
 * quantise the edge to whole pixels — visibly worse on a soft hair edge, which
 * is exactly where choke earns its keep.
 *
 * IDENTITY IS FREE. feather 0 and choke 0 return the input canvas untouched,
 * so the default path costs nothing and nothing to reason about.
 *
 * Public API — window.VbrMatteEdge:
 *   apply(matteCanvas, { feather, choke }) → canvas
 *       feather 0..10  — edge softness in pixels
 *       choke  -10..10 — negative grows the subject, positive shrinks it
 *   isIdentity(opts)   → bool
 *   defaults()         → { feather: 0, choke: 0 }
 */
(function (root) {
    'use strict';

    function defaults() { return { feather: 0, choke: 0 }; }

    function isIdentity(o) {
        if (!o) return true;
        return !(+o.feather > 0) && Math.abs(+o.choke || 0) < 0.01;
    }

    /* Separable box blur of ONE channel of an RGBA buffer, in place, via a
       running sum — O(pixels) regardless of radius, which is what keeps a
       10-px feather on 1080p as cheap as a 1-px one. */
    function boxBlurAlpha(data, w, h, r) {
        if (r < 1) return;
        var n = w * h;
        var a = new Float32Array(n), tmp = new Float32Array(n);
        var i, x, y, row, sum, span;

        for (i = 0; i < n; i++) a[i] = data[i * 4 + 3];

        // horizontal
        span = r * 2 + 1;
        for (y = 0; y < h; y++) {
            row = y * w;
            sum = 0;
            for (x = -r; x <= r; x++) sum += a[row + Math.min(w - 1, Math.max(0, x))];
            for (x = 0; x < w; x++) {
                tmp[row + x] = sum / span;
                sum -= a[row + Math.min(w - 1, Math.max(0, x - r))];
                sum += a[row + Math.min(w - 1, Math.max(0, x + r + 1))];
            }
        }
        // vertical
        for (x = 0; x < w; x++) {
            sum = 0;
            for (y = -r; y <= r; y++) sum += tmp[Math.min(h - 1, Math.max(0, y)) * w + x];
            for (y = 0; y < h; y++) {
                a[y * w + x] = sum / span;
                sum -= tmp[Math.min(h - 1, Math.max(0, y - r)) * w + x];
                sum += tmp[Math.min(h - 1, Math.max(0, y + r + 1)) * w + x];
            }
        }
        for (i = 0; i < n; i++) data[i * 4 + 3] = a[i];
    }

    function apply(matteCanvas, opts) {
        opts = Object.assign(defaults(), opts || {});
        if (isIdentity(opts)) return matteCanvas;

        var feather = Math.max(0, Math.min(10, +opts.feather || 0));
        var choke = Math.max(-10, Math.min(10, +opts.choke || 0));
        var W = matteCanvas.width, H = matteCanvas.height;

        // The blur radius must cover whichever control asked for the most room:
        // choke moves the contour by up to `choke` pixels, and it can only move
        // through pixels the blur actually touched. The third term keeps HEADROOM:
        // with the corrected centre below, f=c=2 at r=2 would leave the subject
        // interior at alpha 191 — measured over the whole −6..6 × 0..10 grid,
        // this term is what brings the violations to zero.
        var radius = Math.max(1, Math.ceil(Math.max(feather, Math.abs(choke), (feather + 2 * Math.abs(choke)) / 2)));

        var out = document.createElement('canvas');
        out.width = W; out.height = H;
        var octx = out.getContext('2d', { willReadFrequently: true });
        octx.drawImage(matteCanvas, 0, 0);
        var img = octx.getImageData(0, 0, W, H);
        var d = img.data;

        boxBlurAlpha(d, W, H, radius);

        // Remap. A box blur of radius r turns a step edge into a ramp of
        // slope 1/(2r+1) per pixel — 2r+1 taps, not 2r — so a feather of f px
        // keeps f/(2r+1) of that ramp, and a choke of c px moves the 50 %
        // contour by c/(2r+1) of it.
        //
        // ⚠️ The first version had both denominators at 2r and an extra ×0.5 on
        // the centre, and the slider therefore moved the edge by HALF of what
        // it said: measured on a hard half-plane, choke 2 shifted the contour
        // 1.00 px and choke 4 shifted it 2.00 px, so a 2-px keyer fringe needed
        // choke 4 to disappear (43 % of the ring survived choke 2). With these
        // constants the shift is exact at every slider step (0.5→0.50, 1→1.00,
        // 2→2.00, 4→3.99, 6→6.00) and the same 2-px ring is 0.9 % at choke 2.
        // Bake-off 2026-09-01 against square erode, disc erode, exact-distance
        // choke and a Gaussian variant: same halo removal, 1.2–5× the cost, and
        // the distance-transform one hardens a soft matte — kept this design.
        var ramp = feather > 0 ? Math.max(0.02, feather / (2 * radius + 1)) : 0.02;
        var centre = 0.5 + choke / (2 * radius + 1);

        var lut = new Uint8Array(256);
        for (var v = 0; v < 256; v++) {
            var a = ((v / 255) - centre) / ramp + 0.5;
            lut[v] = a <= 0 ? 0 : (a >= 1 ? 255 : Math.round(a * 255));
        }
        for (var i = 3; i < d.length; i += 4) d[i] = lut[d[i]];

        octx.putImageData(img, 0, 0);
        return out;
    }

    root.VbrMatteEdge = { apply: apply, isIdentity: isIdentity, defaults: defaults };
})(typeof window !== 'undefined' ? window : globalThis);
